import { alphartisApiAxios } from "@/config/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AUDIT_LOG_SEARCH_RESOURCE } from "../audit-log/use-audit-log-search.query";
import {
  RelocationOrderFlowAction,
  RelocationOrderFlowActionBodyMap,
  RelocationOrderFlowActionInput,
  RelocationOrderFlowResolvable,
  resolveRelocationOrderFlowActionPath,
} from "./_helpers/resolve-relocation-order-flow-path";

const RELOCATION_ORDER_RESOURCE = "relocation-order" as const;

function extractRelocationOrderAndBody<A extends RelocationOrderFlowAction>(
  input: RelocationOrderFlowActionInput<A>,
): { relocationOrder: RelocationOrderFlowResolvable; body?: RelocationOrderFlowActionBodyMap[A] } {
  if ("relocationOrder" in input && "body" in input) {
    return {
      relocationOrder: input.relocationOrder as RelocationOrderFlowResolvable,
      body: input.body as RelocationOrderFlowActionBodyMap[A],
    };
  }

  return { relocationOrder: input as RelocationOrderFlowResolvable };
}

/**
 * Generic mutation hook for relocation order flow actions.
 *
 * The correct endpoint is resolved dynamically from the order's route type and transport method.
 * Actions that require a request body are type-checked at compile time via {@link RelocationOrderFlowActionBodyMap}.
 *
 * @example
 * // Action without body
 * const { mutateAsync } = useRelocationOrderFlowMutation(RelocationOrderFlowAction.BranchAcceptVehicle);
 * await mutateAsync(relocationOrder);
 *
 * @example
 * // Action with body
 * const { mutateAsync } = useRelocationOrderFlowMutation(RelocationOrderFlowAction.TransportCompanyRejectVehicle);
 * await mutateAsync({ relocationOrder, body: { status: "Transport_Company_Rejected_Damaged_Vehicle" } });
 */
export const useRelocationOrderFlowMutation = <A extends RelocationOrderFlowAction>(action: A) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, RelocationOrderFlowActionInput<A>>({
    mutationFn: async (input: RelocationOrderFlowActionInput<A>) => {
      const { relocationOrder, body } = extractRelocationOrderAndBody<A>(input);
      const path = resolveRelocationOrderFlowActionPath(relocationOrder, action);

      await alphartisApiAxios.patch(path, body);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [RELOCATION_ORDER_RESOURCE],
        }),
        queryClient.invalidateQueries({
          queryKey: [AUDIT_LOG_SEARCH_RESOURCE],
        }),
      ]);
    },
  });
};
